import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import "../facility/facility.scss";
import axios, { CancelToken } from "axios";
import { useAppDispatch } from "hooks/redux";
import { DeleteServiceFee, GetServiceFees, PutServiceFee } from "api/rpc/2024-04/facilityAdmin/payment/serviceFee";
import { Select } from "components/select/index";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { showError, showSuccess } from "redux/actions/ui";
import Card from "components/card/Card";
import { isEqualWith } from "lodash";
import Popup from "components/popup/Popup";
import { roundNumber } from "helpers/Helpers";

interface IServiceFeeState {
  type: string;
  amount: number;
  title: string;
  application: string;
}

export default function EditServiceFee() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const { serviceFeeId }: any = useParams();

  const [serviceFeeState, setServiceFeeState] = useState<IServiceFeeState>({
    type: "",
    amount: null,
    title: "",
    application: "",
  });

  const [serviceFeeStateBeforeChanges, setServiceFeeStateBeforeChanges] = useState<IServiceFeeState>(undefined);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadServiceFees(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadServiceFees(token?: CancelToken) {
    const res = await GetServiceFees({ id: serviceFeeId }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      setServiceFeeState(prevState => ({ ...prevState, serviceFees: [] }));
      dispatch(showError(t("Error getting facility service fees")));
      return;
    }

    const data = res.data[0];
    const isPercent = data.type === "percent";

    const amountValue = isPercent ? roundNumber((data.percent ?? 0) * 100, 2) : data.fixed_amount ?? 0;

    setServiceFeeState(prev => ({
      ...prev,
      type: data.type,
      amount: amountValue,
      title: data.title,
      application: data.application,
    }));

    setServiceFeeStateBeforeChanges(prev => ({
      ...prev,
      type: data.type,
      amount: amountValue,
      title: data.title,
      application: data.application,
    }));
  }

  const primaryAction = {
    content: t("Delete"),
    action: () => setShowDeleteWarning(true),
  };

  function unsavedChangesExist() {
    if (serviceFeeStateBeforeChanges === undefined) {
      if (serviceFeeState) {
        setServiceFeeStateBeforeChanges(serviceFeeState);
      }
      return false;
    }

    return !isEqualWith(serviceFeeStateBeforeChanges, serviceFeeState);
  }

  function cancelUnsavedChanges() {
    setServiceFeeState(serviceFeeStateBeforeChanges);
  }

  async function handleSaveServicefee() {
    const decimal = serviceFeeState?.amount / 100;

    const params =
      serviceFeeState?.type === "fixed_amount"
        ? {
          id: serviceFeeId,
          type: "fixed_amount",
          fixed_amount: serviceFeeState?.amount,
          title: serviceFeeState?.title,
          application: "subtotal_price",
        }
        : serviceFeeState?.type === "percent"
          ? {
            id: serviceFeeId,
            type: "percent",
            percent: decimal,
            title: serviceFeeState?.title,
            application: serviceFeeState?.application,
          }
          : null;

    const putRes = await PutServiceFee(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving service fee")));
      return;
    }

    dispatch(showSuccess(t("Successfully saved service fee")));
    setServiceFeeStateBeforeChanges(serviceFeeState);
  }

  async function deleteServiceFee() {
    const params = {
      id: serviceFeeId,
    };

    const deleteRes = await DeleteServiceFee(params, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting service fee")));
      return;
    }

    dispatch(showSuccess(t("Successfully deleted service fee")));
    history.push("/admin/settings/service-fees/");
  }

  return (
    <Page
      title={t("Service Fees")}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: handleSaveServicefee,
        onCancel: cancelUnsavedChanges,
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Service Fees"),
          url: "/admin/settings/service-fees",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                id="title"
                value={serviceFeeState?.title}
                onChange={e => setServiceFeeState(prev => ({ ...prev, title: e.target.value }))}
                label={t("Title")}
              />

              <Input
                id="amount"
                value={serviceFeeState?.amount}
                onChange={e => setServiceFeeState(prev => ({ ...prev, amount: e.target.valueAsNumber }))}
                label={t("Amount")}
                type={"number"}
              />

              {/* <Select
                onChange={(value: number) => setServiceFeeState(prev => ({ ...prev, type: String(value) }))}
                defaultValue={serviceFeeState?.type}
                className="type-select"
                label={t("Type")}
              >
                <Option value={"fixed_amount"}>{t("Fixed")}</Option>
                <Option value={"percent"}>{t("Percent")}</Option>
              </Select> */}
            </FormLayout.Group>
            <FormLayout.Group>
              {serviceFeeState?.type === "percent" ? (
                <Select
                  onChange={(value: number) => setServiceFeeState(prev => ({ ...prev, application: String(value) }))}
                  defaultValue={serviceFeeState?.application}
                  className="type-select"
                  label={t("Application")}
                >
                  <Option value={"total_price"}>{t("Total Price")}</Option>
                  <Option value={"subtotal_price"}>{t("Subtotal Price")}</Option>
                </Select>
              ) : null}
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Popup
        open={showDeleteWarning}
        type="warning"
        title={t("Are you sure?")}
        description={t("Are you sure you want to delete this service fee?")}
        onOk={deleteServiceFee}
        okText={t("Delete")}
        onCancel={() => setShowDeleteWarning(false)}
      />
    </Page>
  );
}
